import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import apiClient from '../utils/apiClient';

/**
 * Transfer / payment plan — in ONE place. (Write-off retired 2026-08-06:
 * move-out closes the ledger identically; per-invoice void covers a
 * staying tenant.)
 *
 * These tools existed only inside the customer's inline rentals row, so
 * opening a rental directly gave you no way to run them. Copying the modals
 * onto the rental page would have been a second implementation to keep in
 * sync — the same mistake that produced three contradicting unit-status
 * palettes and three different draft-counting heuristics. Both surfaces
 * mount this and pass `{kind, rental}`.
 *
 * TRANSFER is a DIFFERENCE adjustment, not a refund-and-rebill. Units swap,
 * the rate moves to the destination unit's, and only the remainder of the
 * current month's rate difference changes hands — invoiced on an upgrade,
 * banked as `credit_balance` on a downgrade, where it auto-applies to the
 * next invoice. Nothing is refunded for the part of the month already paid,
 * because the tenant had a unit for it.
 */
const RentalToolsModal = ({ facilityId, toolModal, onClose, onChanged }) => {
  const [availUnits, setAvailUnits] = useState([]);
  const [toolForm, setToolForm] = useState({});
  const [toolBusy, setToolBusy] = useState(false);

  // Reset per open; load destination units for a transfer.
  useEffect(() => {
    if (!toolModal) return;
    setToolForm({});
    if (toolModal.kind !== 'transfer') return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get(`/api/facilities/${facilityId}/units/`);
        const rows = Array.isArray(res.data) ? res.data : res.data?.units || [];
        if (!cancelled) {
          setAvailUnits(
            rows.filter((u) => u.status === 'Available' && !u.is_archived),
          );
        }
      } catch {
        if (!cancelled) setAvailUnits([]);
      }
    })();
    return () => { cancelled = true; };
  }, [toolModal, facilityId]);

  if (!toolModal) return null;
  const { kind, rental } = toolModal;

  const runTool = async () => {
    setToolBusy(true);
    try {
      if (kind === 'transfer') {
        if (!toolForm.new_unit_id) {
          toast.error('Pick the destination unit.');
          setToolBusy(false);
          return;
        }
        const res = await apiClient.post(
          `/api/facilities/${facilityId}/rentals/${rental._id}/transfer`,
          {
            new_unit_id: toolForm.new_unit_id,
            ...(toolForm.new_rate ? { new_rate: parseFloat(toolForm.new_rate) } : {}),
          },
        );
        const adj = res.data.adjustment;
        toast.success(
          `Transferred to unit ${res.data.to_unit_number}.`
          + (adj > 0 ? ` Adjustment invoiced: $${adj}`
            : adj < 0 ? ` Credit added: $${-adj}` : ''),
        );
      } else if (kind === 'plan') {
        const rows = (toolForm.installments || [])
          .filter((i) => i.due_date && i.amount);
        if (!rows.length) {
          toast.error('Add at least one installment.');
          setToolBusy(false);
          return;
        }
        await apiClient.post(
          `/api/facilities/${facilityId}/rentals/${rental._id}/payment-plan`,
          {
            installments: rows.map((i) => ({
              due_date: i.due_date, amount: parseFloat(i.amount),
            })),
          },
        );
        toast.success("Payment plan saved — late fees pause while it's honored.");
      }
      onClose();
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed.');
    } finally {
      setToolBusy(false);
    }
  };

  const unitNo = rental.unit_number || '—';
  const balance = (rental.balance || 0).toFixed(2);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-md w-full p-5"
        onClick={(e) => e.stopPropagation()}
      >
        {kind === 'transfer' && (
          <>
            <h3 className="text-[14px] font-semibold text-slate-900 mb-1">
              Transfer unit {unitNo}
            </h3>
            <p className="text-[12px] text-slate-500 mb-3">
              Units swap, the rate updates, and the rest of this month&apos;s rate
              difference is invoiced (upgrade) or credited (downgrade).
            </p>
            <label className="block text-[12px] font-medium text-slate-600 mb-1">
              Destination unit
            </label>
            <select
              className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-[13px] mb-3"
              value={toolForm.new_unit_id || ''}
              onChange={(e) => setToolForm({ ...toolForm, new_unit_id: e.target.value })}
            >
              <option value="">Select available unit…</option>
              {availUnits.map((u) => (
                <option key={u._id} value={u._id}>
                  {u.unit_number} ({u.unit_type || '—'}) — $
                  {u.rate_override || u.monthly_rate || '?'}/mo
                </option>
              ))}
            </select>
            <label className="block text-[12px] font-medium text-slate-600 mb-1">
              Rate override (optional — defaults to the unit&apos;s rate)
            </label>
            <input
              type="number" min="0" step="0.01"
              className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-[13px] mb-3"
              value={toolForm.new_rate || ''}
              onChange={(e) => setToolForm({ ...toolForm, new_rate: e.target.value })}
            />
          </>
        )}

        {kind === 'plan' && (
          <>
            <h3 className="text-[14px] font-semibold text-slate-900 mb-1">
              Payment plan — unit {unitNo} (${balance} owed)
            </h3>
            <p className="text-[12px] text-slate-500 mb-3">
              Late fees pause while the plan is honored; a missed installment
              resumes the normal cascade automatically.
            </p>
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-2 mb-2">
                <input
                  type="date"
                  className="border border-slate-300 rounded-md px-2 py-1 text-[13px]"
                  value={toolForm.installments?.[i]?.due_date || ''}
                  onChange={(e) => {
                    const rows = [...(toolForm.installments || [])];
                    rows[i] = { ...(rows[i] || {}), due_date: e.target.value };
                    setToolForm({ ...toolForm, installments: rows });
                  }}
                />
                <input
                  type="number" min="0" step="0.01" placeholder="Amount"
                  className="w-28 border border-slate-300 rounded-md px-2 py-1 text-[13px]"
                  value={toolForm.installments?.[i]?.amount || ''}
                  onChange={(e) => {
                    const rows = [...(toolForm.installments || [])];
                    rows[i] = { ...(rows[i] || {}), amount: e.target.value };
                    setToolForm({ ...toolForm, installments: rows });
                  }}
                />
              </div>
            ))}
          </>
        )}

        <div className="flex justify-end gap-2 mt-2">
          <button
            type="button" onClick={onClose}
            className="px-3.5 py-2 rounded-lg border border-slate-300 bg-slate-200 text-[13px] font-medium text-slate-700 hover:bg-slate-300"
          >
            Cancel
          </button>
          <button
            type="button" onClick={runTool} disabled={toolBusy}
            className="px-3.5 py-2 rounded-lg bg-blue-600 text-white text-[13px] font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            {toolBusy ? 'Working…' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RentalToolsModal;
